import { CloseOutlined, MenuOutlined } from '@ant-design/icons';
import { Button, Grid, Spin, Typography } from 'antd';
import { ReactComponent as LogoutIcon } from 'assets/icons/Logout.svg';
import { ReactComponent as UserCheckIcon } from 'assets/icons/UserCheck.svg';
import clsx from 'clsx';
import NewDossierButton from 'components/NewDossierButton';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { UserOwner } from 'types/User';

const { useBreakpoint } = Grid;

function resolveDesktopBreakpoint(sm?: boolean) {
  if (typeof sm === 'boolean') {
    return sm;
  }

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(min-width: 576px)').matches;
  }

  return false;
}

function getActiveRoute(pathname: string) {
  if (pathname === '/') return '/';
  if (pathname.startsWith('/dossiers')) return '/dossiers';
  if (pathname.startsWith('/ressources')) return '/ressources';
  if (pathname.startsWith('/tableau-des-primes')) return '/tableau-des-primes';
  if (pathname.startsWith('/mon-compte')) return '/mon-compte';
  return pathname;
}

type MobileMenuContextType = {
  isOpen: boolean;
  toggle: () => void;
};

const MobileMenuContext = createContext<MobileMenuContextType | null>(null);

function useMobileMenuContext() {
  const ctx = useContext(MobileMenuContext);
  if (!ctx)
    throw new Error('MobileMenuTrigger must be rendered inside MobileMenu');
  return ctx;
}

export function MobileMenuTrigger() {
  const { isOpen, toggle } = useMobileMenuContext();
  const { t } = useTranslation();

  return (
    <Button
      type="text"
      icon={
        isOpen ? (
          <CloseOutlined className="text-xl text-dark-gray-400" />
        ) : (
          <MenuOutlined className="text-xl text-gris-hellio" />
        )
      }
      className="items-center justify-center !p-0 [&&_.ant-btn-icon]:h-auto"
      onClick={toggle}
      aria-label={isOpen ? t('global:close') : t('menu:open_mobile')}
    />
  );
}

export type MobileMenuItem = {
  key: string;
  icon: React.ElementType;
  label: string;
};

type MobileMenuProps = {
  children: React.ReactNode;
  menuItems: MobileMenuItem[];
  owner?: UserOwner;
  ownerIsLoading: boolean;
  isImpersonated: boolean;
  onLogout: () => void;
};

export default function MobileMenu({
  children,
  menuItems,
  owner,
  ownerIsLoading,
  isImpersonated,
  onLogout,
}: MobileMenuProps) {
  const { t } = useTranslation();
  const { t: tContact } = useTranslation('contact');
  const [isOpen, setIsOpen] = useState(false);
  const { sm } = useBreakpoint();
  const isDesktop = resolveDesktopBreakpoint(sm);
  const history = useHistory();
  const { pathname } = useLocation();

  const activeRoute = useMemo(() => getActiveRoute(pathname), [pathname]);
  const topClass = isImpersonated ? 'top-[116px]' : 'top-16';

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (isDesktop) setIsOpen(false);
  }, [isDesktop]);

  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((v) => !v);

  const handleNavigate = (path: string) => {
    close();
    history.push(path);
  };

  const handleLogout = () => {
    close();
    onLogout();
  };

  return (
    <MobileMenuContext.Provider value={{ isOpen, toggle }}>
      {children}
      {isDesktop ? null : (
        <div
          className={clsx(
            'fixed inset-x-0 bottom-0 z-[999] flex flex-col bg-white',
            'transition-all duration-200 ease-out',
            topClass,
            isOpen
              ? 'translate-y-0 opacity-100'
              : 'pointer-events-none invisible -translate-y-2 opacity-0',
          )}
          role="dialog"
          aria-modal={true}
          aria-hidden={!isOpen}
          aria-label={t('menu:open_mobile')}
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-6 pt-5">
            <nav aria-label={t('menu:open_mobile')}>
              <ul className="flex flex-col gap-1">
                {menuItems.map(({ key, icon: Icon, label }) => {
                  const isActive = activeRoute === key;

                  return (
                    <li key={key}>
                      <button
                        type="button"
                        className={clsx(
                          'flex w-full items-center gap-3 rounded-r-xl border-l-2 px-4 py-4 text-left text-base font-medium transition-colors',
                          isActive
                            ? 'border-bleu-hellio bg-bleu-hellio-1 text-bleu-hellio'
                            : 'border-transparent text-gris-hellio hover:bg-gray-50',
                        )}
                        onClick={() => handleNavigate(key)}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        <Icon
                          className={clsx(
                            'text-lg',
                            isActive ? 'text-bleu-hellio' : 'text-gris-hellio',
                          )}
                        />
                        <span>{label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="mt-4 rounded-2xl bg-vert-transport-1 px-4 py-5">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-vert-transport-10">
                  <UserCheckIcon />
                </span>
                <Typography.Text
                  strong
                  className="!text-base !text-vert-transport-10"
                >
                  {tContact('title')}
                </Typography.Text>
              </div>

              <div className="mt-4">
                {ownerIsLoading && (
                  <div className="flex justify-center py-4">
                    <Spin />
                  </div>
                )}

                {!ownerIsLoading && !owner?.email && (
                  <Typography.Paragraph className="!mb-0 !text-base !text-gris-hellio">
                    {tContact('no_contact')}
                  </Typography.Paragraph>
                )}

                {!ownerIsLoading && owner?.email && (
                  <div className="space-y-1">
                    <Typography.Paragraph className="!mb-0 !text-base !text-gris-hellio">
                      {owner.firstname} {owner.lastname}
                    </Typography.Paragraph>
                    <Typography.Link
                      href={`mailto:${owner.email}`}
                      className="!block !text-base !text-bleu-hellio"
                    >
                      {owner.email}
                    </Typography.Link>
                    <Typography.Link
                      href={`tel:${owner.phone}`}
                      className="!block !text-base !text-gris-hellio hover:!text-gris-hellio"
                    >
                      {owner.phone}
                    </Typography.Link>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 [&>a]:block [&>a]:w-full [&_.ant-btn]:!flex [&_.ant-btn]:!h-14 [&_.ant-btn]:!w-full [&_.ant-btn]:!items-center [&_.ant-btn]:!justify-center [&_.ant-btn]:!text-base [&_.ant-btn]:!font-semibold">
              <NewDossierButton />
            </div>

            <div className="mt-auto flex justify-center pt-10">
              <Button
                type="text"
                icon={<LogoutIcon className="text-lg" />}
                className="!flex !h-auto items-center gap-3 !text-base !font-semibold !text-bleu-hellio"
                onClick={handleLogout}
              >
                {t('menu:logout')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </MobileMenuContext.Provider>
  );
}
